import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, query, where, setDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { Container, Card, Table, Spinner, Alert, Form, Button, Modal, Row, Col, InputGroup, Badge, DropdownButton, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const permissionMap = [
  { label: '積水ハウス', index: 0 },
  { label: '旭化成', index: 1 },
  { label: '住友林業', index: 2 },
  { label: 'その他', index: 3 },
];

// 【追加】会社名、支店名、部署名を連結して整形するヘルパー関数
const formatFullCompanyName = (user) => {
  const parts = [user.companyName, user.branchName, user.departmentName];
  return parts.filter(Boolean).join(' '); // 空でない要素だけをスペースで連結
};

function UserAdmin() {
  const [users, setUsers] = useState([]);
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingData, setEditingData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPhoneParts, setEditPhoneParts] = useState({ part1: '', part2: '', part3: '' });

  const fetchUsersAndCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const usersSnapshotPromise = getDocs(collection(db, 'users'));
      const companiesSnapshotPromise = getDocs(collection(db, 'companies'));
      const [usersSnapshot, companiesSnapshot] = await Promise.all([usersSnapshotPromise, companiesSnapshotPromise]);

      const companiesDataMap = new Map();
      companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
      setCompaniesMap(companiesDataMap);

      const usersList = usersSnapshot.docs.map(doc => {
        const userData = doc.data();
        const companyData = companiesDataMap.get(userData.companyId) || {};
        return {
          id: doc.id,
          ...userData,
          companyName: companyData.companyName,
          companyPrefix: companyData.companyPrefix,
          companySuffix: companyData.companySuffix,
          isSoleProprietor: companyData.isSoleProprietor,
          // ★ ユーザー側の情報を優先し、なければ会社側（移行期間用）を表示
          branchName: userData.branchName || companyData.branchName || '',
          departmentName: userData.departmentName || companyData.departmentName || ''
        };
      });
      setUsers(usersList);
    } catch (err) {
      setError('ユーザー情報の取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'ユーザー管理 | My Order System';
    fetchUsersAndCompanies();
  }, [fetchUsersAndCompanies]);

  useEffect(() => {
    if (editingData) {
      const { part1, part2, part3 } = editPhoneParts;
      const combinedPhone = [part1, part2, part3].filter(Boolean).join('-');
      if (editingData.phone !== combinedPhone) {
        setEditingData(prev => ({ ...prev, phone: combinedPhone }));
      }
    }
  }, [editPhoneParts, editingData]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const lowercasedFilter = searchTerm.toLowerCase();
    return users.filter(user =>
      (user.personName?.toLowerCase() || '').includes(lowercasedFilter) ||
      (user.email?.toLowerCase() || '').includes(lowercasedFilter) ||
      (user.companyName?.toLowerCase() || '').includes(lowercasedFilter) ||
      (user.branchName?.toLowerCase() || '').includes(lowercasedFilter) || // 検索対象に追加
      (user.departmentName?.toLowerCase() || '').includes(lowercasedFilter) // 検索対象に追加
    );
  }, [users, searchTerm]);

  const handleShowEditModal = (user) => {
    setEditingData(user);
    const parts = (user.phone || '').split('-');
    setEditPhoneParts({ part1: parts[0] || '', part2: parts[1] || '', part3: parts[2] || '' });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingData(null);
    setEditPhoneParts({ part1: '', part2: '', part3: '' });
  };

  const handleEditDataChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newEditingData = { ...editingData, [name]: type === 'checkbox' ? checked : value };

    if (name === 'companyPrefix' && value) newEditingData.companySuffix = '';
    if (name === 'companySuffix' && value) newEditingData.companyPrefix = '';
    if (name === 'isSoleProprietor' && checked) {
      newEditingData.companyPrefix = '';
      newEditingData.companySuffix = '';
    }
    setEditingData(newEditingData);
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    setEditPhoneParts(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  const handlePermissionChange = (index) => {
    setEditingData(prev => {
      const codeArray = (prev.userAttributeCode || '0000').split('');
      codeArray[index] = codeArray[index] === '1' ? '0' : '1';
      return { ...prev, userAttributeCode: codeArray.join('') };
    });
  };

  const handleUpdate = async () => {
    if (!editingData) return;
    try {
      const userDocRef = doc(db, 'users', editingData.id);

      let companyIdToSet = null;
      const companiesRef = collection(db, "companies");

      // 1. 会社・支店・部署の組み合わせが一致する既存の会社情報を検索
      const q = query(
        companiesRef,
        where("companyName", "==", editingData.companyName || ""),
        where("companyPrefix", "==", editingData.companyPrefix || ""),
        where("companySuffix", "==", editingData.companySuffix || ""),
        where("branchName", "==", editingData.branchName || ""),
        where("departmentName", "==", editingData.departmentName || ""),
        where("isSoleProprietor", "==", !!editingData.isSoleProprietor)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // 一致する会社情報が見つかった場合、そのIDを使用
        companyIdToSet = querySnapshot.docs[0].id;
        // 共通項目（法人格など）の変更があれば更新
        await updateDoc(doc(db, 'companies', companyIdToSet), {
          companyPrefix: editingData.companyPrefix || "",
          companySuffix: editingData.companySuffix || "",
          isSoleProprietor: !!editingData.isSoleProprietor,
        });
      } else {
        // 一致する情報がない場合、新しい会社ドキュメントを作成
        companyIdToSet = uuidv4();
        const newCompanyData = {
          companyId: companyIdToSet,
          companyName: editingData.companyName || "",
          companyPrefix: editingData.companyPrefix || "",
          companySuffix: editingData.companySuffix || "",
          branchName: editingData.branchName || "",
          departmentName: editingData.departmentName || "",
          isSoleProprietor: !!editingData.isSoleProprietor,
          createdAt: serverTimestamp(),
        };
        await setDoc(doc(db, 'companies', companyIdToSet), newCompanyData);
      }

      // 2. ユーザー情報の更新 (★ branchName, departmentName は含めない)
      const userDataToUpdate = {
        personName: editingData.personName || "",
        phone: editingData.phone || "",
        zipCode: editingData.zipCode || "",
        address1: editingData.address1 || "",
        address2: editingData.address2 || "",
        address3: editingData.address3 || "",
        userAttribute: editingData.userAttribute,
        userAttributeCode: editingData.userAttributeCode,
        companyId: companyIdToSet,
        branchName: deleteField(),
        departmentName: deleteField(),
      };
      await updateDoc(userDocRef, userDataToUpdate);

      handleCloseEditModal();
      fetchUsersAndCompanies();
      alert('情報を更新しました。');
    } catch (err) {
      alert('情報の更新に失敗しました。');
      console.error(err);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'suspended' ? 'active' : 'suspended';
    const confirmationMessage = newStatus === 'suspended' ? `ユーザー「${user.personName}」のアカウントを停止しますか？` : `ユーザー「${user.personName}」のアカウントを有効にしますか？`;
    if (window.confirm(confirmationMessage)) {
      try {
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, { status: newStatus });
        fetchUsersAndCompanies();
      } catch (err) {
        alert('ステータスの変更に失敗しました。');
        console.error(err);
      }
    }
  };

  const handlePostalCodeSearch = useCallback(async () => {
    if (editingData && editingData.zipCode && editingData.zipCode.length === 7) {
      try {
        const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: editingData.zipCode } });
        if (res.data.results) {
          const result = res.data.results[0];
          setEditingData(prev => ({ ...prev, address1: result.address1, address2: result.address2 + result.address3, address3: '' }));
        } else { alert('該当する住所が見つかりませんでした。'); }
      } catch (err) { console.error("住所の取得に失敗しました", err); alert('住所の自動入力に失敗しました。'); }
    } else { alert('7桁の郵便番号を入力してください。'); }
  }, [editingData]);

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">
            ユーザー管理
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="氏名、メールアドレス、会社名などで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
            {
              loading ?
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
                :
                (
                  <>
                    {/* --- PC向けテーブル表示 (md以上の画面で表示) --- */}
                    <div className="d-none d-md-block border">
                      <Table
                        striped
                        bordered
                        hover
                        responsive
                        className='my-5'
                      >
                        <thead>
                          <tr>
                            <th>ステータス</th>
                            <th>氏名</th>
                            <th>会社・組織名</th>
                            <th>メールアドレス</th>
                            <th>権限</th>
                            <th>操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map(user => (
                            <tr key={user.id}>
                              <td>
                                <Badge bg={user.status === 'suspended' ? 'danger' : 'success'}>
                                  {user.status === 'suspended' ? '停止中' : '有効'}
                                </Badge>
                              </td>
                              <td>
                                {user.personName}
                              </td>
                              {/* 【変更点】ヘルパー関数を使って会社・支店・部署名を表示 */}
                              <td>
                                {formatFullCompanyName(user)}
                              </td>
                              <td>
                                {user.email}
                              </td>
                              <td>
                                {(user.userAttributeCode || '0000').split('').map((flag, index) =>
                                  flag === '1' ?
                                    <Badge key={index} bg="secondary" className="me-1 fw-normal">
                                      {permissionMap[index]?.label}
                                    </Badge>
                                    :
                                    null
                                )}
                              </td>
                              <td>
                                <DropdownButton
                                  id={`dropdown-${user.id}`}
                                  title="操作"
                                  size="sm"
                                  variant="outline-secondary"
                                  className='py-3'
                                >
                                  <Dropdown.Item onClick={() => handleShowEditModal(user)}>
                                    編集
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    onClick={() => handleToggleStatus(user)}
                                    className={user.status === 'suspended' ? 'text-success' : 'text-danger'}
                                  >
                                    {user.status === 'suspended' ? '有効化' : '停止'}
                                  </Dropdown.Item>
                                </DropdownButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* --- スマートフォン向けカード表示 (md未満の画面で表示) --- */}
                    <div className="d-md-none">
                      {filteredUsers.map(user => (
                        <Card key={user.id} className="mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <Card.Title className="mb-1">{user.personName}</Card.Title>
                                {/* 【変更点】ヘルパー関数を使って会社・支店・部署名を表示 */}
                                <Card.Subtitle
                                  className="mb-2 text-muted">
                                  {formatFullCompanyName(user) || '会社情報なし'}
                                </Card.Subtitle>
                              </div>
                              <Badge bg={user.status === 'suspended' ? 'danger' : 'success'}>
                                {user.status === 'suspended' ? '停止中' : '有効'}
                              </Badge>
                            </div>
                            <p className="mb-2" style={{ wordBreak: 'break-all' }}>
                              {user.email}
                            </p>
                            <div className="mb-3">
                              {(user.userAttributeCode || '0000').split('').map((flag, index) =>
                                flag === '1' ?
                                  <Badge key={index} bg="secondary" className="me-1 fw-normal">
                                    {permissionMap[index]?.label}
                                  </Badge>
                                  :
                                  null
                              )}
                            </div>
                            <DropdownButton
                              id={`dropdown-mobile-${user.id}`}
                              title="操作"
                              size="sm"
                              variant="outline-secondary"
                              className="w-100 py-5"
                            >
                              <Dropdown.Item onClick={() => handleShowEditModal(user)}>
                                編集
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={() => handleToggleStatus(user)}
                                className={user.status === 'suspended' ?
                                  'text-success'
                                  :
                                  'text-danger'}
                              >
                                {user.status === 'suspended' ? '有効化' : '停止'}
                              </Dropdown.Item>
                            </DropdownButton>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
            <hr className="my-4" />
            <Link to="/admin">
              管理者メニューに戻る
            </Link>
          </Card.Body>
        </Card>

        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              ユーザー・会社情報編集
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {
              editingData &&
              (
                <Tabs defaultActiveKey="user-info" id="edit-tabs" className="mb-3" justify>
                  <Tab eventKey="user-info" title="ユーザー情報">
                    <Form as={Card.Body}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              氏名
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="personName"
                              value={editingData.personName || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group></Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              メールアドレス
                            </Form.Label>
                            <Form.Control type="email" value={editingData.email || ''} readOnly disabled />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              電話番号
                            </Form.Label>
                            <Row className="g-2 align-items-center">
                              <Col>
                                <Form.Control
                                  type="tel"
                                  name="part1"
                                  value={editPhoneParts.part1}
                                  onChange={handlePhoneChange}
                                  maxLength="5"
                                  placeholder="090" />
                              </Col>
                              <Col xs="auto" className="px-0">
                                -
                              </Col>
                              <Col>
                                <Form.Control
                                  type="tel"
                                  name="part2"
                                  value={editPhoneParts.part2}
                                  onChange={handlePhoneChange}
                                  maxLength="4"
                                  placeholder="1234"
                                />
                              </Col>
                              <Col xs="auto" className="px-0">
                                -
                              </Col>
                              <Col>
                                <Form.Control
                                  type="tel"
                                  name="part3"
                                  value={editPhoneParts.part3}
                                  onChange={handlePhoneChange}
                                  maxLength="4"
                                  placeholder="5678"
                                />
                              </Col>
                            </Row>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              郵便番号
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                name="zipCode"
                                value={editingData.zipCode || ''}
                                onChange={handleEditDataChange}
                                maxLength="7"
                              />
                              <Button variant="secondary" onClick={handlePostalCodeSearch}>
                                住所検索
                              </Button>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              住所1(都道府県)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="address1"
                              value={editingData.address1 || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              住所2(市区町村以降)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="address2"
                              value={editingData.address2 || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              住所3(ビル名等)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="address3"
                              value={editingData.address3 || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>
                  </Tab>
                  <Tab eventKey="company-info" title="会社・権限情報">
                    <Form as={Card.Body}>
                      <Row>
                        <Col md={12}>
                          <Form.Group
                            className="mb-3">
                            <Form.Check
                              type="checkbox"
                              name="isSoleProprietor"
                              label="個人事業主"
                              checked={!!editingData.isSoleProprietor}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              会社名
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="companyName"
                              value={editingData.companyName || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              法人格 (前)
                            </Form.Label>
                            <Form.Select
                              name="companyPrefix"
                              value={editingData.companyPrefix || ''}
                              onChange={handleEditDataChange}
                              disabled={!!editingData.isSoleProprietor}
                            >
                              <option value="">
                                なし
                              </option>
                              <option value="株式会社">
                                株式会社
                              </option>
                              <option value="有限会社">
                                有限会社
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              法人格 (後)
                            </Form.Label>
                            <Form.Select
                              name="companySuffix"
                              value={editingData.companySuffix || ''}
                              onChange={handleEditDataChange}
                              disabled={!!editingData.isSoleProprietor}
                            >
                              <option value="">
                                なし
                              </option>
                              <option value="株式会社">
                                株式会社
                              </option>
                              <option value="有限会社">
                                有限会社
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              支店名
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="branchName"
                              value={editingData.branchName || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              部署名
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="departmentName"
                              value={editingData.departmentName || ''}
                              onChange={handleEditDataChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              ユーザー属性
                            </Form.Label>
                            <Form.Select
                              name="userAttribute"
                              value={editingData.userAttribute}
                              onChange={handleEditDataChange}
                            >
                              <option value="一般ユーザー">
                                一般ユーザー
                              </option>
                              <option value="社員">
                                社員
                              </option>
                              <option value="出荷担当者">
                                出荷担当者
                              </option>
                              <option value="管理者">
                                管理者
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              ユーザー権限
                              <Badge bg="info" pill>
                                {editingData.userAttributeCode}
                              </Badge>
                            </Form.Label>
                            <div className="d-flex flex-wrap">
                              {permissionMap.map(perm => (
                                <Form.Check
                                  className="me-3"
                                  inline
                                  key={perm.index}
                                  type="checkbox"
                                  id={`perm-${perm.index}`}
                                  label={perm.label}
                                  checked={(editingData.userAttributeCode || '0000')[perm.index] === '1'}
                                  onChange={() => handlePermissionChange(perm.index)}
                                />
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>
                  </Tab>
                </Tabs>
              )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>キャンセル</Button>
            <Button variant="primary" onClick={handleUpdate}>保存</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </main>
  );
}

export default UserAdmin;